import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useRedux } from "../../../states/redux-state";
import * as tagSelectors from "../../../shared/db/tags-labels-selectors";
import { iFullStoreState, UserAuth, ItemType } from "../../../shared/interfaces";
import LocalPersonTile from "../../pages/PersonTabPage/LocalPeopleTile";
import GalleryModal from "../GalleryModal/GalleryModal";
import { ACL, UserCan } from "../../../shared/constants";

import { FileValue } from "./types";
import { filterEmptyValues } from "./extraInfo.utils";
import { sortLabels } from "./extraInfo.helpers";
import { formatLabelForGrid } from "./extraInfo.formatters";
import { copyDeviceExtraInfo, exportDeviceExtraInfoCsv } from "./ExtraInfoActions/extraInfo.actions";
import ExtraInfoActions from "./ExtraInfoActions/ExtraInfoActions";

import styles from "./extra-info.module.scss";

interface iProps {
  editUrl: string;
  labels: [string, string | object][];
  role?: boolean;
  readOnly?: boolean;
  itemId?: string;
  itemType?: ItemType;
  itemName?: string;
  isPinned?: boolean;
  property?: string;
  imageClick?: (src: string) => any;
  title?: string;
  cssOrder?: number;
  showPin?: boolean;
  withMarginBottom?: boolean;
  formatNumbersByKind?: boolean;
}

export const ExtraInfo = ({
  editUrl,
  labels,
  role,
  itemId,
  itemType,
  itemName,
  isPinned,
  property,
  title,
  cssOrder,
  showPin,
  withMarginBottom = true,
  formatNumbersByKind = false,
}: iProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => (property ? !isPinned : true));
  const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
  const [galleryFiles, setGalleryFiles] = useState<FileValue[]>([]);
  const [galleryIndex, setGalleryIndex] = useState<number>(0);

  const infos = useRedux((s) => s.general.extraInfo);
  const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user);
  const userCanDo = useSelector<iFullStoreState, Array<string>>(
    (s) => s.auth.user?.acl?.can
  );
  const userIsAdmin = ACL.check(UserCan.DO_ANYTHING, userCanDo);
  const canEditInfo = ACL.check(UserCan.CAN_EDIT_INFO, userCanDo);
  const isSuperAdmin = authUser?.beToken?.['do-anything'] || false;

  const filteredLabels = filterEmptyValues(labels);

  useEffect(() => {
    setCollapsed(property ? !isPinned : true);
  }, [isPinned, property]);

  const openGallery = (files: FileValue[], startAt = 0) => {
    if (!files?.length) return;
    setGalleryFiles(files);
    setGalleryIndex(Math.max(0, Math.min(startAt, files.length - 1)));
    setGalleryOpen(true);
  };

  const currentLabels = useRedux(s =>
    tagSelectors.getItemLabelNameValMap(s, { type: itemType, itemId })
  );

  const visibleLabels = sortLabels(
    filteredLabels.filter(([name]) => {
      const labelId = tagSelectors.mapNameToIdValue(name, infos) || name;
      return !infos[labelId]?.details?.isHidden;
    }),
    infos
  );

  const hiddenLabels = sortLabels(
    filteredLabels.filter(([name]) => {
      const labelId = tagSelectors.mapNameToIdValue(name, infos) || name;
      return infos[labelId]?.details?.isHidden;
    }),
    infos
  );

  const sortedLabelsForCopy = [...visibleLabels, ...hiddenLabels];

  return (
    <>
      <LocalPersonTile
        collapsible
        initialCollapsed={collapsed}
        title={title}
        action={
          !role && canEditInfo &&(
            <ExtraInfoActions
              onCopy={() =>
                copyDeviceExtraInfo(
                  sortedLabelsForCopy,
                  infos,
                  authUser,
                  isSuperAdmin,
                  currentLabels,
                  { itemName, itemType }
                )
              }
              onExport={() =>
                exportDeviceExtraInfoCsv(sortedLabelsForCopy, infos, authUser, isSuperAdmin, currentLabels)
              }
              editUrl={editUrl}
            />
          )
        }
        isPinned={isPinned}
        withPin={showPin}
        property={property}
        order={cssOrder}
        tileBodyClassName={styles.tileBody}
        withMarginBottom={withMarginBottom}
      >
        <section className={styles.extraInfoContent}>
          {filteredLabels.length === 0 ? (
            "None Yet"
          ) : (
            <>
              {/* Visible labels */}
              <div className={styles.gridContainer}>
                {visibleLabels.map((label, index) =>
                  formatLabelForGrid(label, index, infos, authUser, currentLabels, openGallery, formatNumbersByKind)
                )}
              </div>

              {/* Hidden labels (admins only) */}
              {userIsAdmin && hiddenLabels.length > 0 && (
                <>
                  <div className={styles.hiddenLabelsSeparator}>Hidden</div>

                  <div className={styles.gridContainer}>
                    {hiddenLabels.map((label, index) =>
                      formatLabelForGrid(label, index, infos, authUser, currentLabels, openGallery, formatNumbersByKind)
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </LocalPersonTile>

      <GalleryModal
        open={galleryOpen}
        files={galleryFiles}
        initialIndex={galleryIndex}
        onClose={() => setGalleryOpen(false)}
        onDeleteAt={null}
        onIndexChange={setGalleryIndex}
      />
    </>
  );
};